/**
 * AI Product Mapping — restore a grid from a persisted run.
 *
 * The run-detail endpoint (GET .../run/history/{runId}) returns stored result rows instead
 * of a live SSE stream, so reviewing a past run needs no re-run. Each stored row carries the
 * raw CSV line (tab-joined) + match/alternatives + the user's saved state; we re-derive the
 * same grid-row shape the live stream produces (reusing mapMatchEvent/mapNoMatchEvent so the
 * hi/low/ambiguous classification stays identical) and rebuild the per-column `fields` from
 * the run's column resolution — the portal mirror of the server's RowFieldExtractor.
 */

import { LOGICAL_FIELDS } from './constants';
import { mapMatchEvent, mapNoMatchEvent } from './runEvents';
import type { ColumnResolution, RunDetailItem } from '../api/types';
import type { GridRow, RunStatus } from './types';

// Backend run status (ProductMatchingRunStatus) → the run-page slice's run.status vocabulary,
// so MappingProgress / the live dot / download-enabled read a restored run correctly.
const RUN_STATUS_MAP: Record<string, RunStatus> = {
  completed: 'complete',
  failed: 'error',
  cancelled: 'cancelled',
  canceled: 'cancelled',
  processing: 'running',
  awaitingcolumns: 'awaiting_columns',
  queued: 'connecting',
  validating: 'connecting',
  // Parked product-first run — restores into the REST review surface; without this
  // mapping a parked restore would fail closed and render as 'error'.
  awaitingproductdecisions: 'awaiting_products',
};

// Fail closed: an unknown/missing status must not read as a finished run (which would
// enable download and hide the failure), so anything unmapped restores as 'error'.
export const mapDetailRunStatus = (status: string | null | undefined): RunStatus =>
  RUN_STATUS_MAP[String(status || '').toLowerCase()] || 'error';

// Rebuild a row's per-field display map from its cells + the run's bindings. Multi-column
// fields (Title/Description/Category) are joined, matching how the matcher concatenated them.
export function fieldsFromCells(
  cells: readonly string[],
  resolution: ColumnResolution | null | undefined,
): Record<string, string> {
  const bindings = resolution?.bindings || [];
  const fields: Record<string, string> = {};
  for (const { key } of LOGICAL_FIELDS) {
    const cols = bindings.filter((b) => b.field === key).sort((a, b) => a.columnIndex - b.columnIndex);
    if (!cols.length) continue;
    const value = cols
      .map((b) => (cells[b.columnIndex] ?? '').trim())
      .filter(Boolean)
      .join(', ');
    if (value) fields[key] = value;
  }
  return fields;
}

/** Tab-split fallback for stored rows that predate the `cells` field — lossy when a cell
 *  itself contains a tab, which is exactly why the server added `cells`. */
export const fieldsFromRawRow = (
  rawRow: string | null | undefined,
  resolution: ColumnResolution | null | undefined,
): Record<string, string> => fieldsFromCells(String(rawRow ?? '').split('\t'), resolution);

// One stored result row → a full grid row (not a patch): reuse the live mappers for status +
// candidates, then overlay the reviewer's saved decisions (confirmed / manual edit / opt-out).
export function mapRunDetailRow(item: RunDetailItem, resolution: ColumnResolution | null | undefined): GridRow {
  // Prefer the server's lossless cells (rawRow re-splitting breaks on in-cell tabs); the
  // raw cells also feed the CSV export when a reviewed run is downloaded.
  const raw = item.cells?.length ? item.cells : String(item.rawRow ?? '').split('\t');
  const fields = fieldsFromCells(raw, resolution);
  const status = String(item.status || '').toLowerCase();
  const alternatives = (item.alternatives || []).map((a) => ({
    sku: a.sku,
    productId: a.productId,
    variantId: a.variantId,
    title: a.title,
    productType: a.productType,
    typeNoun: a.typeNoun,
    typeAgreement: a.typeAgreement,
    confidence: a.confidence,
    scores: a.scores,
  }));

  let patch;
  if (status === 'match' && item.match) {
    patch = mapMatchEvent({
      lineIdx: item.lineIdx,
      sku: item.match.sku,
      productId: item.match.productId,
      title: item.match.title,
      confidence: item.match.confidence,
      source: item.match.source,
      scores: item.match.scores,
      // "match" vs "variant_tie_break" — restored rows explain a tie-break commit the
      // same way live frames do (older stored rows have no reason; the mapper defaults).
      reason: item.reason || undefined,
      type: item.type,
      alternatives,
      fields,
      row: raw,
    });
  } else {
    patch = mapNoMatchEvent({
      lineIdx: item.lineIdx,
      // Older servers didn't store the decision reason; the mapper's fallback then infers
      // no_candidates vs below_threshold from the alternatives list, as it does live.
      reason: status === 'error' ? 'row_error' : item.reason || undefined,
      error: status === 'error' ? item.error || 'Row could not be processed' : null,
      type: item.type,
      alternatives,
      fields,
      row: raw,
    });
  }

  // Distill the pre-re-match snapshot for the hover: the reason plus the SKU/confidence
  // the grid displayed then (a match's own, or a no-match row's top suggestion).
  const previous = item.previous
    ? {
        reason: item.previous.reason ?? null,
        matchedSku: item.previous.match?.sku ?? item.previous.alternatives?.[0]?.sku ?? null,
        confidence: item.previous.match?.confidence ?? item.previous.alternatives?.[0]?.confidence ?? null,
      }
    : undefined;

  const row = { confirmed: false, edited: false, ...patch, ...(previous ? { previous } : {}) } as GridRow;
  const us = item.userState || {};
  if (us.manualEditSelectedSku) {
    return {
      ...row,
      edited: true,
      confirmed: false,
      matchedSku: us.manualEditSelectedSku,
      status: 'hi',
      confidence: 1,
    };
  }
  return { ...row, confirmed: Boolean(us.confirmed) };
}

export const mapRunDetailRows = (
  items: RunDetailItem[] | null | undefined,
  resolution: ColumnResolution | null | undefined,
): GridRow[] => (items || []).map((item) => mapRunDetailRow(item, resolution));
